import { useGetEquipmentFormOptions } from '@/equipments/application/adapters/getEquipmentFormOptions';
import { useMemo } from 'react';
import { useEquipmentFilters } from '@/equipments/presentation/hooks/useEquipmentFilters';
import { buildEquipmentListWhereFilter } from '@/equipments/application/adapters/listEquipments';
import { useListJobEquipments } from '@/jobs/application/adapters/getJobEquipments';
import { SelectedEquipment } from '../RaptorJobCreateModal';
import { useEquipmentSelectorFilters } from './useEquipmentSelectorFilters';

const EQUIPMENT_QUERY_LIMIT = 100;
const DEFAULT_START_YEAR = 1990;

export interface OptionType {
  id?: string | number | null;
  name?: string | null;
}

interface UseEquipmentSelectorProps {
  selectedClass: OptionType | null;
  setSelectedClass: (value: OptionType | null) => void;
  selectedType: OptionType | null;
  setSelectedType: (value: OptionType | null) => void;
  selectedSize: OptionType | null;
  setSelectedSize: (value: OptionType | null) => void;
  setSelectedEquipment: (equipment: SelectedEquipment | null) => void;
  equipmentSearchTerm: string;
  startDate?: Date | null;
  endDate?: Date | null;
  recurrence: 'single' | 'multi';
}

export const useEquipmentSelector = ({
  setSelectedEquipment,
  startDate,
  endDate,
  recurrence,
  selectedClass,
  setSelectedClass,
  selectedType,
  setSelectedType,
  selectedSize,
  setSelectedSize,
  equipmentSearchTerm
}: UseEquipmentSelectorProps) => {
  const {
    availableMakes,
    availableModels,
    availableClasses,
    availableTypes,
    availableSizes,
    availableStatuses,
    loading: loadingEquipmentFormOptions
  } = useGetEquipmentFormOptions(false);

  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: currentYear - DEFAULT_START_YEAR + 1 }, (_, i) => ({
    id: currentYear - i,
    name: String(currentYear - i)
  }));

  const { makeFilters, modelFilters, yearFilters, classFilters, statusFilters } = useEquipmentFilters({
    availableMakes,
    availableModels,
    availableYears,
    availableClasses,
    availableStatuses
  });

  const selectedClassFilters =
    selectedClass && selectedClass.id
      ? classFilters.map((filter) => ({
          ...filter,
          checked: filter.id === selectedClass.id
        }))
      : classFilters;

  const buildWhereClause = () => {
    const baseWhere = buildEquipmentListWhereFilter(
      equipmentSearchTerm,
      makeFilters,
      modelFilters,
      yearFilters,
      selectedClassFilters,
      statusFilters
    );
    const additionalConditions = [
      ...(selectedType && selectedType.id ? [{ type: { id: { eq: Number(selectedType.id) } } }] : []),
      ...(selectedSize && selectedSize.id ? [{ size: { id: { eq: Number(selectedSize.id) } } }] : [])
    ];

    if (additionalConditions.length > 0) {
      const merged = { ...baseWhere };
      merged.and = merged.and ? [...merged.and, ...additionalConditions] : additionalConditions;
      return merged;
    }
    return baseWhere;
  };

  const utcStartDate = startDate
    ? new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0))
    : undefined;

  const getUtcEndDate = () => {
    if (recurrence === 'multi') {
      return endDate ? new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59)) : undefined;
    }
    return startDate ? new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 23, 59, 59)) : undefined;
  };

  const { equipments: apiEquipments, loading: loadingEquipments } = useListJobEquipments(
    {
      where: buildWhereClause(),
      skip: 0,
      take: EQUIPMENT_QUERY_LIMIT
    },
    utcStartDate,
    getUtcEndDate()
  );

  const deduplicatedEquipments = useMemo(() => {
    if (!apiEquipments) return [];
    const seen = new Set<number>();
    return apiEquipments.filter((equipment) => {
      if (seen.has(equipment.id)) return false;
      seen.add(equipment.id);
      return true;
    });
  }, [apiEquipments]);

  const {
    filteredClasses,
    filteredTypes,
    filteredSizes,
    handleClassChange,
    handleTypeChange,
    handleSizeChange,
    handleEquipmentSelect,
    handleEquipmentDeselect,
    handleClearFilters
  } = useEquipmentSelectorFilters({
    availableClasses,
    availableTypes,
    availableSizes,
    selectedClass,
    selectedType,
    selectedSize,
    equipments: deduplicatedEquipments,
    setSelectedClass,
    setSelectedType,
    setSelectedSize,
    setSelectedEquipment
  });

  const hasActiveFilters = selectedClass !== null || selectedType !== null || selectedSize !== null;

  return {
    filteredClasses,
    filteredTypes,
    filteredSizes,
    handleClassChange,
    handleTypeChange,
    handleSizeChange,
    handleEquipmentSelect,
    handleEquipmentDeselect,
    handleClearFilters,
    apiEquipments: deduplicatedEquipments,
    hasActiveFilters,
    loadingEquipments,
    loadingEquipmentFormOptions
  };
};
